import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";

const MAX_ATTEMPTS = 3;
const BLOCK_DURATION = 300; // 5 minutos en segundos

const ForgotPassword = ({ onBackToLogin }) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Estado para bloqueo
    const [attempts, setAttempts] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        let timer;
        if (isBlocked && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && isBlocked) {
            setIsBlocked(false);
            setAttempts(0);
        }
        return () => clearInterval(timer);
    }, [isBlocked, timeLeft]);

    const generateEmailTemplate = (newPassword, userEmail) => {
        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
        }
        .container {
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f9f9f9;
        }
        .header {
            background-color: #0033A0;
            color: white;
            padding: 20px;
            text-align: center;
            border-radius: 5px 5px 0 0;
        }
        .content {
            background-color: white;
            padding: 30px;
            border-radius: 0 0 5px 5px;
        }
        .password-box {
            background-color: #e7f3ff;
            border-left: 4px solid #0033A0;
            padding: 15px;
            margin: 20px 0;
            font-size: 18px;
            font-weight: bold;
            text-align: center;
        }
        .warning {
            background-color: #fff3cd;
            border-left: 4px solid #ffc107;
            padding: 15px;
            margin: 20px 0;
        }
        .footer {
            text-align: center;
            margin-top: 20px;
            font-size: 12px;
            color: #666;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Recuperación de Contraseña</h1>
        </div>
        <div class="content">
            <p>Hola,</p>
            <p>Has solicitado recuperar tu contraseña para el sistema <strong>Admin Domicilios SURA</strong>.</p>
            
            <p>Tu nueva contraseña temporal es:</p>
            <div class="password-box">
                ${newPassword}
            </div>
            
            <div class="warning">
                <strong>⚠️ Recomendaciones de seguridad:</strong>
                <ul>
                    <li>Cambia esta contraseña después de iniciar sesión</li>
                    <li>No compartas esta contraseña con nadie</li>
                    <li>Usa una contraseña única y segura</li>
                </ul>
            </div>
            
            <p>Si no solicitaste este cambio, por favor contacta al administrador del sistema inmediatamente.</p>
            
            <p>Saludos,<br>
            <strong>Equipo de Admin Domicilios SURA</strong></p>
        </div>
        <div class="footer">
            <p>Este es un correo automático, por favor no responder.</p>
            <p>© ${new Date().getFullYear()} Admin Domicilios SURA</p>
        </div>
    </div>
</body>
</html>
        `.trim();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isBlocked) return;

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            // Paso 1: Consultar si el correo existe y obtener nueva contraseña
            const recoveryResponse = await axios.post(
                //"https://helpharmadomicilios.cielum.co/webhook-test/PRO-3effcbc6-c19c-415e-a746-fb40c1e10f33", //test
                "https://helpharmadomicilios.cielum.co/webhook/PRO-3effcbc6-c19c-415e-a746-fb40c1e10f33", //produccion
                {
                    correo: email
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            const data = recoveryResponse.data;

            // Verificar si hubo error
            if (data && (data.error === 1 || data.error === "1")) {
                throw new Error(data.message || "Error al procesar la solicitud");
            }

            // Paso 2: Si todo salió bien, enviar el correo
            const emailTemplate = generateEmailTemplate(data.password, data.email);

            await axios.post(
                "https://helpharmadomicilios.cielum.co/webhook/QA-90b51a38-232c-4a7a-abcd-4-envio-recuperacion-email",
                {
                    template: emailTemplate,
                    asunto: "Recuperación de Contraseña - Admin Domicilios SURA",
                    para: data.email
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    auth: {
                        username: "DOMICILIOSAPI_operador_logistico",
                        password: "DOMICILIOSAPI_operador_logistico"
                    }
                }
            );

            // Éxito
            setSuccess(`Se ha enviado un correo a ${email} con tu nueva contraseña. Por favor revisa tu bandeja de entrada.`);
            setEmail("");
            setAttempts(0);

        } catch (err) {
            console.error("Error en recuperación:", err);
            const newAttempts = attempts + 1;
            setAttempts(newAttempts);

            let msg = err.message || "Error en el servidor";
            if (err.response && err.response.data && err.response.data.message) {
                msg = err.response.data.message;
            }

            setError(msg);

            if (newAttempts >= MAX_ATTEMPTS) {
                setIsBlocked(true);
                setTimeLeft(BLOCK_DURATION);
                setError(`Demasiados intentos fallidos. Bloqueado por ${Math.floor(BLOCK_DURATION / 60)} minutos.`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="auth-background">
            <div className="glass-card-auth">
                <div className="auth-logo">🔑</div>
                <h3 className="auth-title">Recuperar Contraseña</h3>
                <p className="auth-subtitle">Te enviaremos una nueva contraseña a tu correo</p>

                {error && <div className="alert alert-danger" style={{ fontSize: '0.85rem' }}>{error}</div>}
                {success && <div className="alert alert-success" style={{ fontSize: '0.85rem' }}>{success}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="input-group-premium">
                        <label className="input-label">Correo Electrónico</label>
                        <span className="input-icon">✉️</span>
                        <input
                            type="email"
                            className="form-control input-premium"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isBlocked || isLoading}
                            placeholder="tu@correo.com"
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-premium w-100 mb-3"
                        disabled={isBlocked || isLoading}
                    >
                        {isBlocked
                            ? `⏳ Bloqueado - Espera ${formatTime(timeLeft)}`
                            : isLoading
                                ? <><span className="spinner-premium"></span>Procesando...</>
                                : "Recuperar Contraseña"
                        }
                    </button>

                    <button
                        type="button"
                        className="btn btn-link w-100"
                        onClick={onBackToLogin}
                        disabled={isLoading}
                    >
                        ← Volver al inicio de sesión
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;
